
import React from "react";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { DetailedOpeningHours } from "@/types/terrace";
import { UseFormReturn } from "react-hook-form";

interface OpeningHoursFormProps {
  form: UseFormReturn<any>;
}

type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const dayLabels: Record<DayOfWeek, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

const OpeningHoursForm = ({ form }: OpeningHoursFormProps) => {
  const weekdays: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday'];
  const weekends: DayOfWeek[] = ['friday', 'saturday', 'sunday'];

  const renderDayRow = (day: DayOfWeek) => (
    <div key={day} className="flex items-center gap-2 py-1.5 min-h-[40px]">
      <div className="w-8 text-sm font-medium text-muted-foreground">
        {dayLabels[day]}
      </div>
      
      <FormField
        control={form.control}
        name={`detailedOpeningHours.${day}.isOpen`}
        render={({ field }) => (
          <FormItem className="flex items-center space-y-0">
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                className="scale-75"
              />
            </FormControl>
          </FormItem>
        )}
      />

      {form.watch(`detailedOpeningHours.${day}.isOpen`) ? (
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <FormField
            control={form.control}
            name={`detailedOpeningHours.${day}.from`}
            render={({ field }) => (
              <FormItem className="flex-1 min-w-0">
                <FormControl>
                  <Input
                    type="text"
                    placeholder="09:00"
                    className="h-8 text-sm min-w-[60px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <span className="text-sm text-muted-foreground px-1">-</span>
          <FormField
            control={form.control}
            name={`detailedOpeningHours.${day}.to`}
            render={({ field }) => (
              <FormItem className="flex-1 min-w-0">
                <FormControl>
                  <Input
                    type="text"
                    placeholder="17:00"
                    className="h-8 text-sm min-w-[60px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      ) : (
        <span className="text-sm text-muted-foreground flex-1">Closed</span>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <FormLabel className="text-base font-medium">Opening Hours</FormLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">Mon - Thu</div>
          <div className="space-y-2">
            {weekdays.map(renderDayRow)}
          </div>
        </div>
        
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">Fri - Sun</div>
          <div className="space-y-2">
            {weekends.map(renderDayRow)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningHoursForm;
